import { getUserConsents, acceptConsents } from '@api/users/users.service';
import { useMutation, useQuery } from '@tanstack/react-query';
import { UseFormReturn, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ResponseErrorDto } from '@interfaces/models';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '@shared/stores';
import { Routes } from '@shared/enums';
import { toast } from '@ui/components';
import { TFunction } from 'i18next';
import { useEffect } from 'react';
import * as z from 'zod';

import { useGetTermsAndConditions } from './use-get-terms-and-policy';

const sendAcceptConsentsDefaultValues = {
  termsAccepted: {
    accepted: false,
    documentCode: '',
  },
  dataProcessingAccepted: {
    accepted: false,
    documentCode: '',
  },
  walletTermsAccepted: {
    accepted: false,
    documentCode: '',
  },
  marketingPhoneManagementAccepted: false,
  marketingEmailManagementAccepted: false,
};

const sendAcceptConsentsFormSchema = (t: TFunction) =>
  z.object({
    termsAccepted: z.object({
      accepted: z.boolean().refine((val) => val === true, {
        message: t('FORMS.REQUIRED'),
      }),
      documentCode: z.string(),
    }),
    dataProcessingAccepted: z.object({
      accepted: z.boolean().refine((val) => val === true, {
        message: t('FORMS.REQUIRED'),
      }),
      documentCode: z.string(),
    }),
    walletTermsAccepted: z.object({
      accepted: z.boolean().refine((val) => val === true, {
        message: t('FORMS.REQUIRED'),
      }),
      documentCode: z.string(),
    }),
    marketingPhoneManagementAccepted: z.boolean(),
    marketingEmailManagementAccepted: z.boolean(),
  });

export type SendAcceptConsentsForm = z.infer<ReturnType<typeof sendAcceptConsentsFormSchema>>;

export const useAcceptConsents = (): {
  onSubmit: (data: SendAcceptConsentsForm) => void;
  form: UseFormReturn<SendAcceptConsentsForm>;
  isAcceptConsentsLoading: boolean;
  isConsentsLoading: boolean;
} => {
  const termsAndPolicyData = useGetTermsAndConditions();
  const { updateUser } = useUserStore();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const form = useForm<SendAcceptConsentsForm>({
    resolver: zodResolver(sendAcceptConsentsFormSchema(t)),
    defaultValues: sendAcceptConsentsDefaultValues,
    mode: 'onBlur',
  });

  const onSubmit = (data: SendAcceptConsentsForm): void => {
    mutate(data);
  };

  const { mutate, isPending: isAcceptConsentsLoading } = useMutation({
    mutationFn: acceptConsents,
    onSuccess: () => {
      updateUser({ hasAllRequiredConsentsAccepted: true });
      navigate({ pathname: Routes.ROOT });
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const { data: consents, isLoading: isConsentsLoading } = useQuery({
    queryKey: ['user-consents'],
    queryFn: getUserConsents,
  });

  useEffect(() => {
    if (termsAndPolicyData) {
      const { dataProcessingAgreement, termsOfServicesBusiness, businessWalletTerms } =
        termsAndPolicyData;

      form.setValue('dataProcessingAccepted.documentCode', dataProcessingAgreement?.code ?? '');
      form.setValue('walletTermsAccepted.documentCode', businessWalletTerms?.code ?? '');
      form.setValue('termsAccepted.documentCode', termsOfServicesBusiness?.code ?? '');
    }
  }, [termsAndPolicyData, form]);

  useEffect(() => {
    if (consents) {
      form.setValue(
        'marketingPhoneManagementAccepted',
        consents.marketingPhoneManagementAccepted ?? false,
      );
      form.setValue(
        'marketingEmailManagementAccepted',
        consents.marketingEmailManagementAccepted ?? false,
      );
      form.setValue('dataProcessingAccepted.accepted', consents.dataProcessingAccepted ?? false);
      form.setValue('walletTermsAccepted.accepted', consents.walletTermsAccepted ?? false);
      form.setValue('termsAccepted.accepted', consents.termsAccepted ?? false);
    }
  }, [consents, form]);

  return { form, onSubmit, isAcceptConsentsLoading, isConsentsLoading };
};
